import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitFormCheckout } from "../services/Checkout/CheckoutService";
import { savePendingVNPayOrder } from "../services/Checkout/PendingOrderStorage";
import { removeFromCart } from "../services/Cart/CartServices";
import useCheckoutProductStore from "../stores/CheckoutProduct/useCheckoutProductStore";
import useCartStore from "../stores/Cart/useCartStore";

/**
 * Custom hook để quản lý form submission logic
 */
export const useOrderSubmission = (
  checkoutProducts,
  subtotal,
  shippingCost,
  createOrderFromCheckout,
  updateOrderWithPaymentInfo
) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);

  const total = subtotal + shippingCost;

  const validateForm = (formData, shippingLocation = {}) => {
    // Personal info validation
    if (
      !formData.email ||
      !formData.phone ||
      !formData.firstName ||
      !formData.lastName
    ) {
      alert("Vui lòng điền đầy đủ thông tin cá nhân");
      return false;
    }

    // Shipping address validation
    if (
      !formData.shippingAddress ||
      !shippingLocation?.selectedProvince ||
      !shippingLocation?.selectedDistrict ||
      !shippingLocation?.selectedWard ||
      !formData.shippingZipCode
    ) {
      alert(
        "Vui lòng điền đầy đủ địa chỉ giao hàng và chọn tỉnh/huyện/phường, nhập mã zip"
      );
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Vui lòng nhập email hợp lệ");
      return false;
    }

    // Phone validation
    const phoneRegex = /^(0|\+84)[0-9]{9,10}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert("Vui lòng nhập số điện thoại hợp lệ (VD: 0934970856)");
      return false;
    }

    // Cart validation
    if (!checkoutProducts || checkoutProducts.length === 0) {
      alert("Giỏ hàng trống. Vui lòng thêm sản phẩm trước khi thanh toán.");
      return false;
    }

    return true;
  };

  const buildEnhancedFormData = (formData, shippingLocation = {}) => {
    
    return {
      ...formData,
      // Sử dụng shipping address làm địa chỉ chính
      address: formData.shippingAddress,
      city: shippingLocation?.selectedProvince?.name || formData.shippingCity,
      state: shippingLocation?.selectedDistrict?.name || formData.shippingState,
      ward: shippingLocation?.selectedWard?.name || formData.shippingWard,
      zipCode: formData.shippingZipCode,
      Country: formData.shippingCountry,
      fullAddress: `${formData.shippingAddress}, ${
        shippingLocation?.selectedWard?.name || ""
      }, ${shippingLocation?.selectedDistrict?.name || ""}, ${
        shippingLocation?.selectedProvince?.name || ""
      }`,
      provinceId: shippingLocation?.selectedProvince?.id,
      districtId: shippingLocation?.selectedDistrict?.id,
      wardId: shippingLocation?.selectedWard?.id,
      // Shipping address details
      shippingCity:
        shippingLocation?.selectedProvince?.name || formData.shippingCity,
      shippingState:
        shippingLocation?.selectedDistrict?.name || formData.shippingState,
      shippingWard:
        shippingLocation?.selectedWard?.name || formData.shippingWard,
      shippingFullAddress: `${formData.shippingAddress}, ${
        shippingLocation?.selectedWard?.name || ""
      }, ${shippingLocation?.selectedDistrict?.name || ""}, ${
        shippingLocation?.selectedProvince?.name || ""
      }`,
      shippingProvinceId: shippingLocation?.selectedProvince?.id,
      shippingDistrictId: shippingLocation?.selectedDistrict?.id,
      shippingWardId: shippingLocation?.selectedWard?.id,
    };
  };

  const getCheckoutCartKeys = () =>
    (checkoutProducts || [])
      .filter((item) => item.checkout_source === "cart")
      .map((item) => item.local_key || item.cart_item_key)
      .filter(Boolean);

  const cleanupCheckedOutCartItems = async (serverCheckedOutItems = []) => {
    const cartStore = useCartStore.getState();
    const checkoutCartKeys = getCheckoutCartKeys();

    if (cartStore.isGuestMode) {
      cartStore._clearGuestCartItems(checkoutCartKeys);
      return;
    }

    const serverCartKeys = serverCheckedOutItems
      .map((item) => item.cart_item_key)
      .filter(Boolean);
    const cartKeys = serverCartKeys.length > 0 ? serverCartKeys : checkoutCartKeys;

    if (cartKeys.length > 0) {
      await Promise.all(
        cartKeys.map((cartItemKey) => removeFromCart({ cart_item_key: cartItemKey }))
      );
      await cartStore.showCart();
    }
  };

  const handleSubmit = async (formData, shippingLocation) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    let keepSubmitDisabled = false;

    try {
      setIsSubmitting(true);

      // Validation
      if (!validateForm(formData, shippingLocation)) {
        return;
      }

      const enhancedFormData = buildEnhancedFormData(formData, shippingLocation);

      createOrderFromCheckout(enhancedFormData, {
        subtotal,
        shippingCost,
        total,
      });


      const response = await submitFormCheckout(
        enhancedFormData,
        checkoutProducts,
        {
          subtotal,
          shippingCost,
          total,
        },
        shippingLocation
      );

      if (response && (response.success || response.result === "success")) {
        // Update order với thông tin từ server
        const updatedOrder = updateOrderWithPaymentInfo({
          order_id: response.orderId,
          transaction_no: response.transaction_no,
          amount: response.amount || total,
          payment_date: response.payment_date || new Date().toISOString(),
          status: "processing",
        });

        // Navigate với order data
        if (
          formData.paymentMethod === "vnpay_visa" ||
          formData.paymentMethod === "vnpay" ||
          formData.paymentMethod === "paypal"
        ) {
          if (
            formData.paymentMethod === "vnpay_visa" ||
            formData.paymentMethod === "vnpay"
          ) {
            savePendingVNPayOrder(response, checkoutProducts);
          }

          // Lưu order data vào sessionStorage để preserve qua redirect
          sessionStorage.setItem(
            "currentOrderData",
            JSON.stringify(updatedOrder)
          );
          window.location.href = response.redirectUrl || response.payment_url;
        } else if (formData.paymentMethod === "cod") {
          const successMarker = {
            order_id: response.orderId,
            order_number: response.orderNumber || response.orderId,
            payment_method: "cod",
            completed_at: new Date().toISOString(),
          };

          sessionStorage.setItem(
            "lianne_cod_order_success",
            JSON.stringify(successMarker)
          );
          sessionStorage.removeItem("lianne_cod_idempotency_key");
          useCheckoutProductStore.getState().clearCheckoutItemsOnly();
          setOrderSuccess(true);

          await cleanupCheckedOutCartItems(response.checkedOutItems || []);

          // Prepare data for final payment page
          const navigationData = {
            orderData: updatedOrder,
            paymentInfo: {
              status: "success",
              order_id: response.orderId,
              payment_method: "cod",
              amount: response.total || total,
              order_number: response.orderNumber || response.orderId,
              currency: response.currency || "VND",
              payment_date: successMarker.completed_at,
            },
          };


          setTimeout(() => {
            navigate("/final-payment", {
              state: navigationData,
              replace: true,
            });
          }, 300);
        }
      } else {

        let errorMessage = "There was an error processing your payment.";

        if (response && response.messages) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(response.messages, "text/html");
          const errorElements = doc.querySelectorAll("li");
          if (errorElements.length > 0) {
            errorMessage = Array.from(errorElements)
              .map((el) => el.textContent)
              .join("\n");
          }
        } else if (response && response.message) {
          errorMessage = response.message;
        }

        alert(errorMessage);

        if (response?.isRateLimited) {
          keepSubmitDisabled = true;
          setTimeout(() => {
            setIsSubmitting(false);
          }, response.retryAfterMs || 30000);
        }
      }
    } catch (error) {
      alert(`Lỗi xử lý thanh toán: ${error.message || error}`);
    } finally {
      // Reset loading state sau 3 giây nếu chưa navigate (cho COD)
      if (keepSubmitDisabled) {
        return;
      }

      if (formData.paymentMethod !== "cod") {
        setIsSubmitting(false);
      } else {
        setTimeout(() => {
          setIsSubmitting(false);
          setOrderSuccess(false);
        }, 3000);
      }
    }
  };

  return {
    isSubmitting,
    orderSuccess,
    handleSubmit,
  };
};
